import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import config from '../config/config';
import useAnalytics from '../hooks/useAnalytics';

interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}

const ContactModal: React.FC<ContactModalProps> = ({ open, onClose }) => {
  const { trackInteraction } = useAnalytics();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Spåra när modalen öppnas
  useEffect(() => {
    if (open) {
      trackInteraction('contact_modal_opened');
    }
  }, [open, trackInteraction]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = `${config.api.endpoints.contact}/submit`;
      console.log('Försöker skicka till URL:', url);
      console.log('Data som skickas:', formData);
      
      trackInteraction('contact_form_submit_attempt');

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      console.log('Svar från server:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server response error:', response.status, errorData);
        trackInteraction('contact_form_submit_error', { error: errorData.error });
        throw new Error(errorData.error || 'Något gick fel');
      }

      const responseData = await response.json();
      console.log('Lyckat svar från server:', responseData);

      trackInteraction('contact_form_submit_success');

      // Visa success-meddelande
      alert('Tack för ditt meddelande! Vi återkommer så snart som möjligt.');
      onClose();
    } catch (err) {
      console.error('Error submitting form:', err);
      const error = err as Error;
      console.error('Full error details:', {
        message: error?.message || 'Unknown error',
        stack: error?.stack || 'No stack trace'
      });
      trackInteraction('contact_form_submit_error', { error: error?.message });
      alert('Ett fel uppstod när meddelandet skulle skickas. Vänligen försök igen.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="contact-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: 400 },
        maxHeight: { xs: '95vh', sm: '90vh' },
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: { xs: 2, sm: 4 },
        borderRadius: 2,
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px'
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555'
          }
        }
      }}>
        <IconButton
          aria-label="stäng"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: 4, sm: 8 },
            top: { xs: 4, sm: 8 },
            color: 'grey.500'
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Typography 
          id="contact-modal-title" 
          variant="h6" 
          component="h2" 
          gutterBottom
          sx={{ 
            mb: 3,
            pr: 4 // Ge plats för stäng-knappen
          }}
        >
          Kontakta oss
        </Typography>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              required
              fullWidth
              label="Namn"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ 
                '& .MuiInputBase-root': {
                  borderRadius: 1
                }
              }}
            />
            <TextField
              required
              fullWidth
              label="E-post"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              sx={{ 
                '& .MuiInputBase-root': {
                  borderRadius: 1
                }
              }}
            />
            <TextField
              required
              fullWidth
              label="Telefonnummer"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              sx={{ 
                '& .MuiInputBase-root': {
                  borderRadius: 1
                }
              }}
            />
            <TextField
              required
              fullWidth
              label="Meddelande"
              name="message"
              multiline
              rows={4}
              value={formData.message}
              onChange={handleChange}
              sx={{ 
                '& .MuiInputBase-root': {
                  borderRadius: 1
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                bgcolor: '#2C3E50',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                '&:hover': {
                  bgcolor: '#34495E'
                }
              }}
            >
              Skicka
            </Button>
          </Stack>
        </form>
      </Box>
    </Modal>
  );
};

export default ContactModal; 